import { Color3, PBRMaterial, type Mesh, type Scene } from "@babylonjs/core";
import { getSurfaceNormal, getSurfaceTextures, type SurfaceTextureId } from "./surface-textures.ts";

/** One small palette keeps every primitive in the same low-poly style. */
export const PALETTE = {
  sand: "#e9d39a",
  wetSand: "#c9b071",
  grass: "#6fae4a",
  grassDark: "#4f8d3a",
  rock: "#8c8a86",
  ice: "#cfe8f3",
  snow: "#eef3f6",
  rockDark: "#6c6a68",
  wood: "#8a5a33",
  woodDark: "#5e3b20",
  woodLight: "#b5834f",
  plank: "#a8784a",
  /** Shadowed timber inside a stove-in hull hole. */
  void: "#140f0b",
  sail: "#f3e6c4",
  rope: "#c8a26a",
  white: "#f4f1ea",
  plaster: "#f4f1ea",
  paper: "#f3e6c4",
  red: "#c8443a",
  paintRed: "#c8443a",
  lava: "#d95a28",
  roof: "#b04a32",
  thatch: "#b79b5e",
  gold: "#f2c14e",
  iron: "#3c3f44",
  steel: "#f4f1ea",
  leaf: "#3f9a43",
  leafDark: "#2f7a35",
  trunk: "#9b6b3e",
  lamp: "#ffd36b",
  cloth: "#2f6fa3",
  dirt: "#7a5530",
  water: "#3aa7d6",
  skin: "#e0b48a",
  hair: "#4a3222",
  leather: "#4b3021",
  pants: "#3e3a4c",
  shirt: "#ece2cc",
  brass: "#c99a3f",
  hat: "#2b2622",
  eye: "#1c1c22",
} as const;

export type PaletteKey = keyof typeof PALETTE;

const CHARACTER_COATS = ["#2f6fa3", "#b5463a", "#4f8a3c", "#8a4fa3"] as const;

/** Identity, not join order or viewpoint, determines a sailor's coat. */
export function characterCoatColor(playerId: string): string {
  let hash = 2166136261;
  for (let i = 0; i < playerId.length; i++) hash = Math.imul(hash ^ playerId.charCodeAt(i), 16777619);
  return CHARACTER_COATS[(hash >>> 0) % CHARACTER_COATS.length]!;
}

export type SurfaceFamily =
  | "wood" | "planks" | "paintedWood" | "fabric" | "rope" | "iron" | "brass" | "gold"
  | "leather" | "skin" | "hair" | "paper" | "plaster" | "eye"
  | "sand" | "ground" | "rock" | "leaf" | "bark" | "clay" | "ice" | "snow" | "thatch"
  | "lamp" | "water" | "lava"
  // Character parts (tools/models/crewmate_custom.py): albedo or relative shading arrives as COLOR_0.
  | "wool" | "velvet" | "knit" | "linen" | "hide" | "flesh" | "fur" | "gloss";

export interface SurfaceProfile {
  readonly texture: SurfaceTextureId | null;
  readonly metres: number;
  readonly roughness: number;
  readonly metallic: number;
  readonly normalStrength: number;
}

/** Metric UVs are divided by metres; the shared texture wrappers stay at unit scale. */
export const SURFACE_PROFILES: Readonly<Record<SurfaceFamily, SurfaceProfile>> = {
  wood: { texture: "wood", metres: 1.6, roughness: 0.82, metallic: 0, normalStrength: 0.5 },
  planks: { texture: "planks", metres: 1.6, roughness: 0.82, metallic: 0, normalStrength: 0.5 },
  paintedWood: { texture: "wood", metres: 1.6, roughness: 0.82, metallic: 0, normalStrength: 0.15 },
  fabric: { texture: "fabric", metres: 0.25, roughness: 0.98, metallic: 0, normalStrength: 0.2 },
  rope: { texture: "rope", metres: 0.12, roughness: 0.96, metallic: 0, normalStrength: 0.2 },
  iron: { texture: "metal", metres: 0.4, roughness: 0.5, metallic: 1, normalStrength: 0.12 },
  brass: { texture: "metal", metres: 0.4, roughness: 0.4, metallic: 1, normalStrength: 0.12 },
  gold: { texture: "metal", metres: 0.4, roughness: 0.32, metallic: 1, normalStrength: 0.12 },
  leather: { texture: "leather", metres: 0.2, roughness: 0.88, metallic: 0, normalStrength: 0.2 },
  skin: { texture: "fine", metres: 0.15, roughness: 0.85, metallic: 0, normalStrength: 0.05 },
  hair: { texture: "fine", metres: 0.1, roughness: 0.95, metallic: 0, normalStrength: 0.05 },
  paper: { texture: "fine", metres: 0.2, roughness: 0.95, metallic: 0, normalStrength: 0.05 },
  plaster: { texture: "fine", metres: 1, roughness: 0.85, metallic: 0, normalStrength: 0.05 },
  eye: { texture: null, metres: 1, roughness: 0.35, metallic: 0, normalStrength: 0 },
  sand: { texture: "sand", metres: 2, roughness: 0.96, metallic: 0, normalStrength: 0.35 },
  ground: { texture: "ground", metres: 3, roughness: 0.95, metallic: 0, normalStrength: 0.35 },
  rock: { texture: "rock", metres: 4, roughness: 0.9, metallic: 0, normalStrength: 0.35 },
  leaf: { texture: "leaf", metres: 0.4, roughness: 0.9, metallic: 0, normalStrength: 0.2 },
  bark: { texture: "bark", metres: 0.5, roughness: 0.9, metallic: 0, normalStrength: 0.5 },
  clay: { texture: "fine", metres: 0.5, roughness: 0.88, metallic: 0, normalStrength: 0.05 },
  ice: { texture: "ice", metres: 1, roughness: 0.35, metallic: 0, normalStrength: 0.15 },
  snow: { texture: "snow", metres: 2, roughness: 0.95, metallic: 0, normalStrength: 0.15 },
  thatch: { texture: "bark", metres: 0.5, roughness: 0.95, metallic: 0, normalStrength: 0.2 },
  lamp: { texture: null, metres: 1, roughness: 0.6, metallic: 0, normalStrength: 0 },
  water: { texture: null, metres: 1, roughness: 0.18, metallic: 0, normalStrength: 0 },
  lava: { texture: null, metres: 1, roughness: 1, metallic: 0, normalStrength: 0 },
  wool: { texture: "character-wool", metres: 0.12, roughness: 0.95, metallic: 0, normalStrength: 0.9 },
  velvet: { texture: "character-wool", metres: 0.12, roughness: 0.82, metallic: 0, normalStrength: 0.3 },
  knit: { texture: "character-knit", metres: 0.06, roughness: 0.97, metallic: 0, normalStrength: 1 },
  linen: { texture: "fortress-fabric", metres: 0.25, roughness: 0.9, metallic: 0, normalStrength: 0.7 },
  hide: { texture: "character-leather", metres: 0.15, roughness: 0.5, metallic: 0, normalStrength: 0.8 },
  flesh: { texture: "character-skin", metres: 0.1, roughness: 0.62, metallic: 0, normalStrength: 0.5 },
  fur: { texture: "character-hair", metres: 0.08, roughness: 0.68, metallic: 0, normalStrength: 0.8 },
  gloss: { texture: null, metres: 1, roughness: 0.1, metallic: 0, normalStrength: 0 },
};

/** Skull opts in without changing the metric UVs or another island's materials. */
export const FORTRESS_PROFILES: Readonly<Record<SurfaceFamily, SurfaceProfile>> = {
  ...SURFACE_PROFILES,
  wood: { ...SURFACE_PROFILES.wood, texture: "fortress-wood", roughness: 0.9, normalStrength: 0.9 },
  // Individual boards already have geometric joints: never paint more seams inside them.
  planks: { ...SURFACE_PROFILES.planks, texture: "fortress-wood", roughness: 0.88, normalStrength: 0.85 },
  paintedWood: { ...SURFACE_PROFILES.paintedWood, texture: "fortress-wood", normalStrength: 0.35 },
  rock: { ...SURFACE_PROFILES.rock, texture: "fortress-rock", roughness: 0.94, normalStrength: 0.85 },
  sand: { ...SURFACE_PROFILES.sand, texture: "fortress-sand", normalStrength: 0.7 },
  ground: { ...SURFACE_PROFILES.ground, texture: "fortress-ground", normalStrength: 0.65 },
  bark: { ...SURFACE_PROFILES.bark, texture: "fortress-bark", roughness: 0.94, normalStrength: 0.85 },
  leaf: { ...SURFACE_PROFILES.leaf, texture: "fortress-leaf", roughness: 0.72, normalStrength: 0.4 },
  fabric: { ...SURFACE_PROFILES.fabric, texture: "fortress-fabric", normalStrength: 0.65 },
  rope: { ...SURFACE_PROFILES.rope, texture: "fortress-rope", normalStrength: 0.75 },
  iron: { ...SURFACE_PROFILES.iron, texture: "fortress-metal", roughness: 0.68, normalStrength: 0.5 },
  brass: { ...SURFACE_PROFILES.brass, texture: "fortress-metal", roughness: 0.54, normalStrength: 0.45 },
};

/** The player ship's opt-in skin: weathered fortress timber and metal, stitched sailcloth, carved gilt. */
export const SHIP_PROFILES: Readonly<Record<SurfaceFamily, SurfaceProfile>> = {
  ...SURFACE_PROFILES,
  wood: { ...SURFACE_PROFILES.wood, texture: "fortress-wood", roughness: 0.88, normalStrength: 0.8 },
  planks: { ...SURFACE_PROFILES.planks, texture: "fortress-wood", normalStrength: 0.75 },
  paintedWood: { ...SURFACE_PROFILES.paintedWood, texture: "fortress-wood", normalStrength: 0.35 },
  fabric: { ...SURFACE_PROFILES.fabric, texture: "ship-sail", metres: 2.0, roughness: 0.95, normalStrength: 0.5 },
  rope: { ...SURFACE_PROFILES.rope, texture: "fortress-rope", normalStrength: 0.75 },
  iron: { ...SURFACE_PROFILES.iron, texture: "fortress-metal", roughness: 0.62, normalStrength: 0.5 },
  brass: { ...SURFACE_PROFILES.brass, texture: "fortress-metal", roughness: 0.5, normalStrength: 0.45 },
  gold: { ...SURFACE_PROFILES.gold, texture: "ship-gilt", metres: 0.5, roughness: 0.42, metallic: 1, normalStrength: 0.35 },
};

/** Shared sandstone tint keeps loose stones and terrain's exposed layers coherent. */
export const FORTRESS_ROCK_COLOR = "#c7a373";

const PALETTE_SURFACES: Record<PaletteKey, SurfaceFamily> = {
  sand: "sand", wetSand: "sand", grass: "ground", grassDark: "ground", dirt: "ground",
  rock: "rock", rockDark: "rock", ice: "ice", snow: "snow",
  wood: "wood", woodDark: "wood", woodLight: "wood", plank: "planks", void: "wood",
  sail: "fabric", cloth: "fabric", shirt: "fabric", pants: "fabric", red: "fabric", hat: "fabric",
  rope: "rope", iron: "iron", steel: "iron", brass: "brass", gold: "gold", leather: "leather",
  skin: "skin", hair: "hair", eye: "eye", white: "plaster", plaster: "plaster", paper: "paper",
  leaf: "leaf", leafDark: "leaf", trunk: "bark", roof: "clay", thatch: "thatch",
  paintRed: "plaster", lava: "lava", lamp: "lamp", water: "water",
};

const MODEL_SURFACES: Record<string, SurfaceFamily> = {
  ...PALETTE_SURFACES,
  crew: "paintedWood",
  crewCloth: "fabric",
  crewSail: "fabric",
  coat: "fabric",
  spray: "water",
  emblem: "fabric",
  coatWool: "wool", coatVelvet: "velvet",
  wool: "wool", velvet: "velvet", knit: "knit", linen: "linen", hide: "hide", fur: "fur", gloss: "gloss",
  skinTone: "flesh", boneTone: "skin", hairTone: "fur", irisTone: "gloss",
};

/**
 * Character slot defaults: `coat*` follow the instance coat colour; skin/hair/iris
 * tones are set per instance by slot name; the rest stay white under their COLOR_0.
 */
const CHARACTER_SLOT_TINTS: Record<string, string> = {
  coatWool: "coat", coatVelvet: "coat",
  skinTone: "#e0b48a", boneTone: "#e8dfc8", hairTone: "#4a3222", irisTone: "#3d6e9c",
  wool: "#ffffff", velvet: "#ffffff", knit: "#ffffff", linen: "#ffffff", hide: "#ffffff", fur: "#ffffff", gloss: "#ffffff",
};

/** Slot semantics are explicit: model colours must never determine their physical material. */
export function surfaceForSlot(slot: string): SurfaceFamily {
  if (!Object.hasOwn(MODEL_SURFACES, slot)) throw new Error(`unknown model material slot "${slot}"`);
  return MODEL_SURFACES[slot]!;
}

/** Instance colour for a model slot: explicit colours, then character channels, then the palette. */
export function slotColor(slot: string, colors: Readonly<Record<string, string>>): string | undefined {
  if (Object.hasOwn(colors, slot)) return colors[slot];
  if (Object.hasOwn(CHARACTER_SLOT_TINTS, slot)) {
    const tint = CHARACTER_SLOT_TINTS[slot]!;
    return tint === "coat" ? colors.coat ?? CHARACTER_COATS[0] : tint;
  }
  return Object.hasOwn(PALETTE, slot) ? PALETTE[slot as PaletteKey] : undefined;
}

/** Broad highlights preserve the palette; only actual metal slots conduct light. */
const SURFACES: Record<PaletteKey, readonly [roughness: number, metallic: number]> = {
  sand: [0.96, 0], wetSand: [0.7, 0], grass: [0.95, 0], grassDark: [0.95, 0],
  ice: [0.35, 0],
  rock: [0.9, 0], rockDark: [0.92, 0], wood: [0.82, 0], woodDark: [0.86, 0],
  woodLight: [0.8, 0], plank: [0.78, 0], void: [0.95, 0], sail: [0.98, 0], rope: [0.96, 0],
  white: [0.85, 0], red: [0.82, 0], roof: [0.88, 0], gold: [0.32, 1],
  iron: [0.5, 1], leaf: [0.9, 0], leafDark: [0.92, 0], trunk: [0.9, 0],
  lamp: [0.6, 0], cloth: [0.98, 0], dirt: [1, 0], water: [0.18, 0],
  skin: [0.85, 0], hair: [0.95, 0], leather: [0.88, 0], pants: [0.98, 0],
  shirt: [0.98, 0], brass: [0.4, 1], hat: [0.94, 0], eye: [0.35, 0],
  steel: [0.5, 1], paper: [0.95, 0], plaster: [0.85, 0], paintRed: [0.82, 0],
  lava: [1, 0], snow: [0.95, 0], thatch: [0.95, 0],
};

export class Materials {
  private readonly cache = new Map<string, PBRMaterial>();

  constructor(private readonly scene: Scene) {}

  get(key: PaletteKey, opts: { emissive?: number; alpha?: number } = {}): PBRMaterial {
    const id = `${key}:${opts.emissive ?? 0}:${opts.alpha ?? 1}`;
    let m = this.cache.get(id);
    if (!m) {
      m = this.color(id, PALETTE[key], opts.emissive ?? 0, opts.alpha ?? 1);
      this.applySurface(m, PALETTE_SURFACES[key]);
      // Primitive variants retain their authored scalar differences.
      [m.roughness, m.metallic] = SURFACES[key];
      if (key === "lamp" || key === "lava") {
        m.emissiveColor = m.albedoColor.scale(opts.emissive ?? 1);
      }
      this.cache.set(id, m);
    }
    return m;
  }

  /** A one-off colour (e.g. avatar coats). */
  color(name: string, hex: string, emissive = 0, alpha = 1): PBRMaterial {
    const m = new PBRMaterial(name, this.scene);
    // Palette hexes are sRGB; PBR shades in linear space.
    const c = Color3.FromHexString(hex).toLinearSpace();
    m.albedoColor = c;
    m.metallic = 0;
    m.roughness = 0.9;
    if (emissive > 0) m.emissiveColor = c.scale(emissive);
    if (alpha < 1) m.alpha = alpha;
    return m;
  }

  /** Shared white surface; models bake their instance tints into linear vertex colours. */
  vertexColored(family: SurfaceFamily): PBRMaterial {
    const id = `vertexColor:${family}`;
    let m = this.cache.get(id);
    if (!m) {
      m = this.color(id, "#ffffff");
      this.applySurface(m, family);
      applyCharacterFinish(m, family);
      this.cache.set(id, m);
    }
    return m;
  }

  /** Cached opt-in fortress skin; authored instance tints remain linear vertex colours. */
  fortress(family: SurfaceFamily): PBRMaterial {
    const id = `fortress:${family}`;
    let m = this.cache.get(id);
    if (!m) {
      m = this.color(id, "#ffffff");
      this.applySurface(m, family, FORTRESS_PROFILES[family]);
      m.forceIrradianceInFragment = true;
      if (family === "leaf") {
        // Geometric blade silhouettes stay opaque and cast normal shadows;
        // transmitted sunlight lights the underside without alpha/refraction.
        m.backFaceCulling = false;
        m.twoSidedLighting = true;
        m.subSurface.isTranslucencyEnabled = true;
        m.subSurface.translucencyIntensity = 0.28;
        m.subSurface.useAlbedoToTintTranslucency = true;
        m.subSurface.translucencyColor = Color3.FromHexString("#d4df9c").toLinearSpace();
        m.subSurface.minimumThickness = 0.002;
        m.subSurface.maximumThickness = 0.008;
      } else if (family === "fabric") {
        m.backFaceCulling = false;
        m.twoSidedLighting = true;
        m.sheen.isEnabled = true;
        m.sheen.intensity = 0.12;
        m.sheen.color = Color3.FromHexString("#e7ddc6").toLinearSpace();
        m.sheen.roughness = 0.85;
      }
      if (family === "lamp") {
        m.unlit = false;
        m.emissiveColor = Color3.FromHexString("#ffd891").toLinearSpace();
        m.emissiveIntensity = 1.15;
      }
      m.metadata = { surfaceFamily: family, surfaceSet: "fortress" };
      this.cache.set(id, m);
    }
    return m;
  }

  /** Cached player-ship skin (SHIP_PROFILES); families the set leaves alone (lamp, water…) stay shared. */
  ship(family: SurfaceFamily): PBRMaterial {
    if (SHIP_PROFILES[family] === SURFACE_PROFILES[family]) return this.vertexColored(family);
    const id = `ship:${family}`;
    let m = this.cache.get(id);
    if (!m) {
      m = this.color(id, "#ffffff");
      this.applySurface(m, family, SHIP_PROFILES[family]);
      m.forceIrradianceInFragment = true;
      if (family === "fabric") {
        m.backFaceCulling = false;
        m.twoSidedLighting = true;
        m.sheen.isEnabled = true;
        m.sheen.intensity = 0.12;
        m.sheen.color = Color3.FromHexString("#e7ddc6").toLinearSpace();
        m.sheen.roughness = 0.85;
      }
      m.metadata = { surfaceFamily: family, surfaceSet: "ship" };
      this.cache.set(id, m);
    }
    return m;
  }

  private applySurface(m: PBRMaterial, family: SurfaceFamily, profile = SURFACE_PROFILES[family]): void {
    m.metadata = { surfaceFamily: family };
    m.roughness = profile.roughness;
    m.metallic = profile.metallic;
    if (profile.texture) {
      const maps = getSurfaceTextures(this.scene, profile.texture);
      m.albedoTexture = maps.albedo;
      m.bumpTexture = getSurfaceNormal(this.scene, profile.texture, profile.normalStrength);
      m.metallicTexture = maps.orm;
      m.useRoughnessFromMetallicTextureAlpha = false;
      m.useRoughnessFromMetallicTextureGreen = true;
      m.useMetallnessFromMetallicTextureBlue = true;
      m.useAmbientOcclusionFromMetallicTextureRed = true;
      // Roughness is filtered against the bump gradient, suppressing the specular
      // shimmer MSAA cannot resolve on tiled normals. Only meaningful with a bump map.
      m.enableSpecularAntiAliasing = true;
      // Match Babylon's glTF convention for unflipped UV0/OpenGL normal maps.
      m.invertNormalMapX = !this.scene.useRightHandedSystem;
      m.invertNormalMapY = this.scene.useRightHandedSystem;
    }
    if (family === "lamp" || family === "lava") {
      m.unlit = true;
      m.emissiveColor = m.albedoColor.clone();
    }
  }
}

/**
 * Character-only PBR responses layered over the shared surface maps: pile sheen
 * on wool and velvet, a lacquer coat on polished hide, wet clearcoat eyes and
 * thin-tissue translucency on skin. Palette families are never touched. Sheen
 * keeps the full albedo (no energy scaling) so cloth colours stay saturated.
 */
function applyCharacterFinish(m: PBRMaterial, family: SurfaceFamily): void {
  const sheen = (intensity: number, roughness: number, hex: string): void => {
    m.sheen.isEnabled = true;
    m.sheen.intensity = intensity;
    m.sheen.roughness = roughness;
    m.sheen.color = Color3.FromHexString(hex).toLinearSpace();
  };
  switch (family) {
    case "wool": sheen(0.12, 0.6, "#d8d0c4"); break;
    case "velvet": sheen(0.3, 0.35, "#b8aeb8"); break;
    case "knit": sheen(0.1, 0.7, "#d8d0c4"); break;
    case "fur": sheen(0.22, 0.4, "#e8d2b4"); break;
    case "hide":
      m.clearCoat.isEnabled = true;
      m.clearCoat.intensity = 0.55;
      m.clearCoat.roughness = 0.28;
      break;
    case "gloss":
      m.clearCoat.isEnabled = true;
      m.clearCoat.intensity = 1;
      m.clearCoat.roughness = 0.04;
      break;
    case "flesh":
      // Ears, nose and fingers glow faintly warm against the sun; the base stays opaque.
      m.subSurface.isTranslucencyEnabled = true;
      m.subSurface.translucencyIntensity = 0.14;
      // Follows each sailor's own skin tone (vertex colour), warmed slightly.
      m.subSurface.useAlbedoToTintTranslucency = true;
      m.subSurface.translucencyColor = Color3.FromHexString("#ffc4b0").toLinearSpace();
      m.subSurface.minimumThickness = 0.004;
      m.subSurface.maximumThickness = 0.03;
      break;
    default:
      return;
  }
  m.metadata = { ...m.metadata, characterFinish: family };
}

/**
 * Box-projects UVs (in `tile`-metre repeats) along each face's dominant normal
 * axis. Use only for box-like runtime primitives, never curved or authored meshes.
 * The own mesh scale is baked into UVs; apply once before static merging.
 */
export function boxProjectUVs(mesh: Mesh, tile = 1.6): void {
  const pos = mesh.getVerticesData("position");
  const nrm = mesh.getVerticesData("normal");
  if (!pos || !nrm) return;
  const s = mesh.scaling;
  const uvs = new Float32Array((pos.length / 3) * 2);
  for (let i = 0, j = 0; i < pos.length; i += 3, j += 2) {
    const x = pos[i]! * s.x;
    const y = pos[i + 1]! * s.y;
    const z = pos[i + 2]! * s.z;
    const ax = Math.abs(nrm[i]!);
    const ay = Math.abs(nrm[i + 1]!);
    const az = Math.abs(nrm[i + 2]!);
    if (ay >= ax && ay >= az) {
      uvs[j] = z / tile;
      uvs[j + 1] = x / tile;
    } else if (ax >= az) {
      uvs[j] = z / tile;
      uvs[j + 1] = y / tile;
    } else {
      uvs[j] = x / tile;
      uvs[j + 1] = y / tile;
    }
  }
  mesh.setVerticesData("uv", uvs, false);
}

/** Preserve the sphere builder's seam-safe UVs, with metric circumference/meridian repeats. */
export function sphereProjectUVs(mesh: Mesh, tile: number): void {
  const positions = mesh.getVerticesData("position");
  const sphereUV = mesh.getVerticesData("uv");
  if (!positions || !sphereUV) throw new Error(`sphere ${mesh.name}: positions and normalized UVs are required`);
  if (!(tile > 0) || !Number.isFinite(tile)) throw new Error(`sphere ${mesh.name}: invalid repeat metres`);
  if (sphereUV.length !== positions.length / 3 * 2) throw new Error(`sphere ${mesh.name}: invalid UV count`);
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    minX = Math.min(minX, positions[i]!); maxX = Math.max(maxX, positions[i]!);
    minY = Math.min(minY, positions[i + 1]!); maxY = Math.max(maxY, positions[i + 1]!);
    minZ = Math.min(minZ, positions[i + 2]!); maxZ = Math.max(maxZ, positions[i + 2]!);
  }
  const rx = (maxX - minX) * Math.abs(mesh.scaling.x) / 2;
  const ry = (maxY - minY) * Math.abs(mesh.scaling.y) / 2;
  const rz = (maxZ - minZ) * Math.abs(mesh.scaling.z) / 2;
  // Ramanujan's ellipse perimeter handles scaled sphere primitives without
  // replacing their pole/seam topology. V spans one half-meridian.
  const perimeter = (a: number, b: number): number => {
    if (a + b === 0) return 0;
    const h = ((a - b) / (a + b)) ** 2;
    return Math.PI * (a + b) * (1 + 3 * h / (10 + Math.sqrt(4 - 3 * h)));
  };
  const circumference = perimeter(rx, rz) / tile;
  const meridian = (perimeter(rx, ry) + perimeter(rz, ry)) / (4 * tile);
  const uvs = new Float32Array(sphereUV.length);
  for (let i = 0; i < sphereUV.length; i += 2) {
    uvs[i] = sphereUV[i]! * circumference;
    uvs[i + 1] = sphereUV[i + 1]! * meridian;
  }
  mesh.setVerticesData("uv", uvs, false);
}
